// Shared helpers for product barcode scanning (camera and hardware wedge scanners).

export function normalize_barcode(barcode = ''){
  // Wedge scanners often append suffix chars like Enter/Tab/newline.
  return barcode
    .split('\r').join('')
    .split('\n').join('')
    .split('\t').join('')
    .replace(/\s+/g, '')
    .trim()
}

// Builds the set of plausible representations of a barcode, so that eg.
// UPC-A (12 digits) matches the same code stored as EAN-13 with a leading zero.
export function barcode_candidates(barcode){
  const normalized = normalize_barcode(barcode)
  const candidates = new Set()
  if(!normalized) return candidates

  candidates.add(normalized)
  const numericRuns = normalized.match(/\d{8,14}/g) || []
  for(const numeric of numericRuns){
    candidates.add(numeric)
    if(numeric.length === 12) candidates.add(`0${numeric}`)
    if(numeric.length === 13 && numeric.startsWith('0')) candidates.add(numeric.slice(1))
  }

  if(/^\d+$/.test(normalized)){
    if(normalized.length === 12) candidates.add(`0${normalized}`)
    if(normalized.length === 13 && normalized.startsWith('0')) candidates.add(normalized.slice(1))
  }

  return candidates
}

// Short codes for shelf labels. Numeric payload keeps Code 128 compact
// (digit pairs compress), the S- prefix separates them from product barcodes.
export function generate_shelf_code(existing = new Set()){
  let code
  do {
    code = `S-${Math.floor(Math.random() * 1e8).toString().padStart(8, '0')}`
  } while(existing.has(code))
  existing.add(code)
  return code
}

export function barcodes_match(scanned, expected){
  if(!scanned || !expected) return false
  const scannedCandidates = barcode_candidates(scanned)
  for(const candidate of barcode_candidates(expected)){
    if(scannedCandidates.has(candidate)) return true
  }
  return false
}

// Plausible tracking numbers behind what was scanned or typed: carrier label barcodes often wrap
// the tracking number (FedEx prints a 34-digit code ending with the 12-digit number, GS1 codes carry
// application identifiers), and people type numbers with the spaces the label shows.
export function tracking_candidates(code = ''){
  const compact = String(code).replace(/[\s-]/g, '').toUpperCase()
  const candidates = new Set()
  if(!compact) return []
  candidates.add(compact)
  const s10 = compact.match(/[A-Z]{2}\d{9}[A-Z]{2}/)
  if(s10) candidates.add(s10[0])
  const digits = compact.replace(/\D/g, '')
  if(/^\d+$/.test(compact) && compact.length > 12) candidates.add(compact.slice(-12))
  if(digits.length >= 20) candidates.add(digits.slice(-20))
  if(compact.startsWith('00') && compact.length === 20) candidates.add(compact.slice(2))
  if(digits.length >= 14 && digits.length !== compact.length) candidates.add(digits)
  return [...candidates].slice(0, 10)
}
